// A chat's knowledge graph as a reader follows it: the machines, sessions and
// people the chat has touched, and what happened in them on the clock.
//
// The graph holds two sorts of anchor. Endpoints, sessions and people are few
// and connected: they are the structure, drawn as nested rows (a machine, the
// people seen on it, its sessions). Processes, messages and prompts are
// events: many, each at a time, and each goes on a wall-clock axis in the row
// of the session it happened in (or of its machine, when no session is known),
// so the relations between the two sorts are the layout rather than lines to
// follow. Query windows are spans on the same axis: the time the chat looked
// at. Every anchor, relation and claim records the write that first brought it
// in (`seq`), so the picture can be shown as it stood after any step, with
// that step's additions marked. Claims, when they come, point into the same
// picture.

const EVENT_KINDS = new Set(["process", "message", "prompt"]);
// The group of what the graph places on no machine it knows.
export const NO_MACHINE = "endpoint:none";

const kindOf = (key) => {
    const i = String(key || "").indexOf(":");
    return i > 0 ? String(key).slice(0, i) : null;
};

const stepIdOf = (sources, seq) => sources?.[0]?.toolCallId || `write:${seq ?? "?"}`;
const claimStepId = (c) => c.source?.toolCallId || `claim:${c.id}`;

const ENTITY_WORDS = {
    PROCESS_EVENT: "process events",
    TELEMETRY_TIMELINE: "telemetry",
    FILE_EVENT: "file events",
    DNS_EVENT: "DNS lookups",
    NETWORK_CONNECTION: "connections",
    ENDPOINT: "endpoints",
    DIRECTORY_USER: "directory",
    PROMPT: "prompts",
};

// What named a session or a machine, said as its source: "named by telemetry".
const SOURCE_WORDS = {
    ENDPOINT: "the endpoint directory",
    DIRECTORY_USER: "the directory",
    TRACE_ACTIVATION: "trace activations",
    PROMPT: "prompt summaries",
};

function sourceWords(entity) {
    const name = String(entity || "").replace(/^ENTITY_TYPE_/, "");
    if (SOURCE_WORDS[name]) return SOURCE_WORDS[name];
    if (/^CONVERSATION_/.test(name)) return "the conversation capture";
    return entityWords(name);
}

function entityWords(entity) {
    const name = String(entity || "").replace(/^ENTITY_TYPE_/, "");
    if (!name) return null;
    if (ENTITY_WORDS[name]) return ENTITY_WORDS[name];
    if (/^CONVERSATION_/.test(name)) return "conversation";
    return name.toLowerCase().replace(/_/g, " ");
}

/**
 * The investigation's steps, in the order they happened: one per tool call
 * that brought in anchors, relations or a claim. Ordered by the write that
 * first brought each in; a graph saved before writes were counted falls back
 * to the order its anchors were added, then its relations, then its claims.
 */
const isCounted = (graph) => [...(graph?.nodes?.values?.() || []), ...(graph?.edges?.values?.() || []), ...(graph?.claims || [])].every((x) => Number.isFinite(x.seq));

export function graphSteps(graph) {
    const nodes = [...(graph?.nodes?.values?.() || [])];
    const edges = [...(graph?.edges?.values?.() || [])];
    const claims = graph?.claims || [];
    const counted = isCounted(graph);
    const steps = new Map();
    const note = (id, order, add) => {
        let s = steps.get(id);
        if (!s) {
            s = { id, order, anchors: 0, kinds: {}, relations: 0, claims: [], entity: null };
            steps.set(id, s);
        }
        s.order = Math.min(s.order, order);
        add(s);
    };
    nodes.forEach((n, i) => note(stepIdOf(n.sources, n.seq), counted ? n.seq : i, (s) => {
        s.anchors += 1;
        s.kinds[n.kind] = (s.kinds[n.kind] || 0) + 1;
        s.entity = s.entity || entityWords(n.sources?.[0]?.entity);
    }));
    edges.forEach((e, i) => note(stepIdOf(e.sources, e.seq), counted ? e.seq : nodes.length + i, (s) => {
        s.relations += 1;
        s.entity = s.entity || entityWords(e.sources?.[0]?.entity);
    }));
    claims.forEach((c, i) => note(claimStepId(c), counted ? c.seq : nodes.length + edges.length + i, (s) => { s.claims.push(c.id); }));
    return [...steps.values()]
        .sort((a, b) => a.order - b.order)
        .map((s, index) => ({ ...s, index, label: s.claims.length && !s.anchors && !s.relations ? `claim ${s.claims.join(", ")}` : s.entity || "query" }));
}

// A window anchor's id is its query range, `start/end`.
function windowSpan(id) {
    const [a, b] = String(id || "").split("/");
    const from = Date.parse(a);
    const to = Date.parse(b);
    return Number.isFinite(from) && Number.isFinite(to) && to >= from ? { from, to } : null;
}

// The span a session was known to cover before write `bound`: its history's
// entry then, or its attributes on a graph saved before it kept one.
function sessionSpan(s, bound) {
    if (Array.isArray(s.spans)) {
        let hit = null;
        for (const [seq, from, to] of s.spans) if (seq < bound) hit = { from, to: Number.isFinite(to) ? to : from };
        return hit;
    }
    if (!Number.isFinite(s.attrs?.at)) return null;
    return { from: s.attrs.at, to: Number.isFinite(s.attrs?.last) ? s.attrs.last : s.attrs.at };
}

const shortId = (id) => String(id || "").slice(0, 8);
const byLabel = (a, b) => a.label.localeCompare(b.label);

/**
 * The picture as it stood after step `step` (the last step when null).
 *
 * groups    one per machine with something in it (plus one for sessions and
 *           events no machine is known for): its label, the people seen on
 *           it, and its rows -- the machine's own row when events ran on it
 *           outside a known session, then one per session. A session row
 *           with no events is one a result named with nothing of it in the
 *           graph yet.
 * rows      { key, kind, label, detail, word, span, events, undated, isNew, named }
 *           (`named`, on a session and a machine: the results that first named
 *           it and their step, { words, step }, for one with nothing in it)
 * events    { key, kind, at, label, reached, reachedKeys, isNew }, in time order
 * machines  the machines reached with nothing in them yet
 * windows   the query ranges, { key, from, to, isNew }
 * axis      { from, to } over the events and session spans, or over the
 *           windows when nothing is dated
 * claims    { id, kind, status, bearing, statement, evidence, isNew }
 * people    the people no machine names; hosts, the hosts reached
 *
 * `freshSteps` (step ids) and `fresh` (anchor keys) mark new beside the
 * step's own additions: a call that ran other calls added across their steps.
 */
export function timelineView(graph, { step = null, fresh = null, freshSteps = null } = {}) {
    const steps = graphSteps(graph);
    const at = steps.length ? Math.min(steps.length - 1, Math.max(0, step ?? steps.length - 1)) : -1;
    const allowed = new Set(steps.slice(0, at + 1).map((s) => s.id));
    const current = at >= 0 ? steps[at].id : null;
    const stepNumber = new Map(steps.map((st) => [st.id, st.index + 1]));
    const namedBy = (n) => (n ? { words: sourceWords(n.sources?.[0]?.entity), step: stepNumber.get(stepIdOf(n.sources, n.seq)) ?? null } : null);
    const isFreshStep = (id) => id === current || Boolean(freshSteps?.has(id));
    const bound = at + 1 < steps.length && isCounted(graph) ? steps[at + 1].order : Infinity;
    const nodes = new Map();
    for (const n of graph?.nodes?.values?.() || []) {
        const id = stepIdOf(n.sources, n.seq);
        if (allowed.has(id)) nodes.set(n.key, { ...n, isNew: isFreshStep(id) || Boolean(fresh?.has(n.key)) });
    }
    const outOf = new Map();
    for (const e of graph?.edges?.values?.() || []) {
        if (!nodes.has(e.from) || !nodes.has(e.to) || !allowed.has(stepIdOf(e.sources, e.seq))) continue;
        const k = `${e.from}|${e.kind}`;
        if (!outOf.has(k)) outOf.set(k, []);
        outOf.get(k).push(e.to);
    }
    const out = (key, rel) => outOf.get(`${key}|${rel}`) || [];
    const labelOf = (key) => {
        const n = nodes.get(key);
        return n?.label || n?.id || key;
    };

    const events = [...nodes.values()].filter((n) => EVENT_KINDS.has(n.kind));
    const sessions = [...nodes.values()].filter((n) => n.kind === "session");
    const sessionOfEvent = new Map(events.map((ev) => [ev.key, out(ev.key, "in_session")[0] || null]));
    const machineOfEvent = new Map(events.map((ev) => [ev.key, out(ev.key, "ran_on")[0] || out(ev.key, "on_endpoint")[0] || null]));
    // A session's machine: the one its rows name, else the one most of its
    // events ran on.
    const machineOfSession = new Map();
    for (const s of sessions) {
        let machine = out(s.key, "on_endpoint")[0] || null;
        if (!machine) {
            const tally = new Map();
            for (const ev of events) {
                if (sessionOfEvent.get(ev.key) !== s.key) continue;
                const m = machineOfEvent.get(ev.key);
                if (m) tally.set(m, (tally.get(m) || 0) + 1);
            }
            machine = [...tally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
        }
        machineOfSession.set(s.key, machine || NO_MACHINE);
    }
    const machineOf = (ev) => {
        const s = sessionOfEvent.get(ev.key);
        return s ? machineOfSession.get(s) : machineOfEvent.get(ev.key) || NO_MACHINE;
    };

    const eventLine = (ev) => ({
        key: ev.key,
        kind: ev.kind,
        at: Number.isFinite(ev.attrs?.at) ? ev.attrs.at : null,
        label: ev.label || shortId(ev.id),
        // Whether the label says something (a command, a summary), not just the id.
        labelled: Boolean(ev.label),
        reached: out(ev.key, "reached").map(labelOf),
        reachedKeys: out(ev.key, "reached"),
        isNew: ev.isNew,
    });
    const rowOf = ({ list, ...row }) => {
        const dated = list.map(eventLine).filter((e) => e.at != null).sort((a, b) => a.at - b.at);
        return { ...row, events: dated, undated: list.length - dated.length };
    };

    const groupKeys = new Set([...nodes.values()].filter((n) => n.kind === "endpoint").map((n) => n.key));
    for (const m of machineOfSession.values()) groupKeys.add(m);
    for (const ev of events) groupKeys.add(machineOf(ev));

    const placed = new Set();
    const groups = [];
    const machines = [];
    for (const gk of groupKeys) {
        const machine = nodes.get(gk) || null;
        const own = events.filter((ev) => !sessionOfEvent.get(ev.key) && machineOf(ev) === gk);
        const mine = sessions.filter((s) => machineOfSession.get(s.key) === gk);
        const people = new Set(machine ? out(gk, "registered_to") : []);
        for (const s of mine) for (const p of out(s.key, "attributed_to")) people.add(p);
        for (const ev of events) if (machineOf(ev) === gk) for (const p of out(ev.key, "ran_as")) people.add(p);
        for (const p of people) placed.add(p);
        const head = {
            key: gk,
            label: machine ? machine.label || shortId(machine.id) : "machine not known",
            people: [...people].map((p) => ({ key: p, label: labelOf(p), isNew: Boolean(nodes.get(p)?.isNew) })).sort(byLabel),
            isNew: machine?.isNew || false,
            named: namedBy(machine),
        };
        if (!own.length && !mine.length) {
            machines.push(head);
            continue;
        }
        const rows = [];
        if (own.length) rows.push(rowOf({ key: gk, kind: "endpoint", label: "no trace known", detail: "", word: null, list: own, span: null, isNew: false }));
        const sessionRows = mine.map((s) => {
            const word = "trace";
            return rowOf({
                key: s.key,
                kind: "session",
                label: s.label || word,
                detail: shortId(s.id),
                word,
                list: events.filter((ev) => sessionOfEvent.get(ev.key) === s.key),
                span: sessionSpan(s, bound),
                isNew: s.isNew,
                named: namedBy(s),
            });
        });
        const start = (r) => r.span?.from ?? r.events[0]?.at ?? Infinity;
        sessionRows.sort((a, b) => start(a) - start(b) || byLabel(a, b));
        rows.push(...sessionRows);
        groups.push({ ...head, rows, count: rows.reduce((n, r) => n + r.events.length + r.undated, 0) });
    }
    // The busiest machines first: that is where the investigation's events are.
    groups.sort((a, b) => b.count - a.count || byLabel(a, b));
    machines.sort(byLabel);

    const windows = [...nodes.values()]
        .filter((n) => n.kind === "window")
        .map((n) => ({ key: n.key, ...windowSpan(n.id), isNew: n.isNew }))
        .filter((w) => Number.isFinite(w.from))
        .sort((a, b) => a.from - b.from);
    let lo = Infinity;
    let hi = -Infinity;
    for (const g of groups) {
        for (const r of g.rows) {
            for (const e of r.events) { lo = Math.min(lo, e.at); hi = Math.max(hi, e.at); }
            if (r.span) { lo = Math.min(lo, r.span.from); hi = Math.max(hi, r.span.to); }
        }
    }
    if (!Number.isFinite(lo)) {
        for (const w of windows) { lo = Math.min(lo, w.from); hi = Math.max(hi, w.to); }
    }
    const axis = Number.isFinite(lo) ? { from: lo, to: hi } : null;

    const claims = (graph?.claims || [])
        .filter((c) => allowed.has(claimStepId(c)))
        .map((c) => ({
            id: c.id,
            kind: c.kind,
            status: c.status,
            bearing: c.bearing === "context" ? "context" : "answers",
            statement: c.statement,
            evidence: (c.evidence || []).filter((k) => nodes.has(k)),
            isNew: isFreshStep(claimStepId(c)),
        }));
    const people = [...nodes.values()]
        .filter((n) => n.kind === "person" && !placed.has(n.key))
        .map((n) => ({ key: n.key, label: n.label || n.id, isNew: n.isNew }))
        .sort(byLabel);
    const hosts = [...nodes.values()]
        .filter((n) => n.kind === "host")
        .map((n) => ({ key: n.key, label: n.label || n.id, isNew: n.isNew }))
        .sort(byLabel);

    return { steps, step: at, groups, machines, windows, axis, claims, people, hosts, anchors: nodes.size };
}

/**
 * Where a tool call sits in the investigation: the steps it wrote, under its
 * own id and under the ids of the calls it ran (`<id>:...`, as a recipe's
 * searches and expands and a fan-out's items are named), as `{ first, step,
 * ids }`. A call known only by the anchors it added (its snapshot's keys)
 * sits at their steps. Null when the graph holds neither, or lacks one of
 * those anchors, which says it is another chat's graph.
 */
export function stepForCall(graph, { toolCallId = null, keys = [] } = {}) {
    const steps = graphSteps(graph);
    const mine = toolCallId ? steps.filter((s) => s.id === toolCallId || s.id.startsWith(`${toolCallId}:`)) : [];
    if (!mine.length && keys.length) {
        const index = new Map(steps.map((s) => [s.id, s]));
        for (const k of keys) {
            const n = graph?.nodes?.get?.(k);
            const s = n ? index.get(stepIdOf(n.sources, n.seq)) : null;
            if (!s) return null;
            if (!mine.includes(s)) mine.push(s);
        }
    }
    if (!mine.length) return null;
    const at = mine.map((s) => s.index);
    return { first: Math.min(...at), step: Math.max(...at), ids: new Set(mine.map((s) => s.id)) };
}

/**
 * What a claim points at in the picture: the events it cites, the rows of the
 * sessions it cites or whose events it cites, the machines it cites or whose
 * people it cites, and the query windows it cites.
 */
export function claimFocus(view, claim) {
    const cited = new Set(claim?.evidence || []);
    const events = new Set();
    const rows = new Set();
    const groups = new Set();
    for (const g of [...(view?.groups || []), ...(view?.machines || [])]) {
        if (cited.has(g.key) || g.people.some((p) => cited.has(p.key))) groups.add(g.key);
        for (const r of g.rows || []) {
            if (cited.has(r.key)) rows.add(r.key);
            for (const e of r.events) {
                if (!cited.has(e.key)) continue;
                events.add(e.key);
                rows.add(r.key);
            }
        }
    }
    const windows = new Set([...cited].filter((k) => kindOf(k) === "window"));
    return { events, rows, groups, windows };
}

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;
const TICK_STEPS = [MINUTE, 5 * MINUTE, 15 * MINUTE, 30 * MINUTE, 60 * MINUTE, 3 * 60 * MINUTE, 6 * 60 * MINUTE, 12 * 60 * MINUTE, DAY];

/**
 * Round-numbered ticks across an axis, at most `max` of them, round on the
 * local clock (`offsetMs` is the zone's offset behind UTC, as
 * getTimezoneOffset gives it, in ms).
 */
export function axisTicks(axis, max = 6, offsetMs = new Date(axis?.from ?? 0).getTimezoneOffset() * MINUTE) {
    if (!axis) return [];
    if (!(axis.to > axis.from)) return [axis.from];
    const span = axis.to - axis.from;
    const stepMs = TICK_STEPS.find((s) => span / s <= max) || Math.ceil(span / max / DAY) * DAY;
    const out = [];
    for (let t = Math.ceil((axis.from - offsetMs) / stepMs) * stepMs + offsetMs; t <= axis.to; t += stepMs) out.push(t);
    return out;
}
